import { useCallback, useEffect, useRef, useState } from "react";
import { getCologuardCampaigns, getCologuardDashboardMetrics } from "../services/api.js";
import { mergeCampaignMetrics } from "../utils/history.js";

const PAGE_SIZE = 10;
export const INITIAL_HISTORY_FILTERS = Object.freeze({
  designator: "",
  status: "",
  createdFrom: "",
  createdTo: "",
});

function normalizeFilters(filters = {}) {
  return {
    designator: String(filters.designator || "").trim().toUpperCase(),
    status: String(filters.status || "").trim().toLowerCase(),
    createdFrom: String(filters.createdFrom || "").trim(),
    createdTo: String(filters.createdTo || "").trim(),
  };
}

function getFilterKey(filters) {
  return JSON.stringify(normalizeFilters(filters));
}

export default function useHistory({ isAuthenticated, accessToken }) {
  const [historyFilters, setHistoryFilters] = useState(INITIAL_HISTORY_FILTERS);
  const [campaignHistoryState, setCampaignHistoryState] = useState({
    loading: false,
    loadingPage: false,
    error: "",
    campaigns: [],
    nextCursor: "",
    hasMore: false,
    currentPage: 1,
    pages: [],
    totalCount: null,
    pageSize: PAGE_SIZE,
  });
  const pagesRef = useRef([]);

  useEffect(() => {
    pagesRef.current = campaignHistoryState.pages;
  }, [campaignHistoryState.pages]);

  const loadCampaignHistory = useCallback(async (filters = INITIAL_HISTORY_FILTERS, options = {}) => {
    if (!isAuthenticated) return;

    const normalizedFilters = normalizeFilters(filters);
    const filterKey = getFilterKey(normalizedFilters);
    const pageNumber = Math.max(1, Number(options.pageNumber) || 1);
    const force = Boolean(options.force);
    const cachedPage = pagesRef.current.find(
      (page) => page.pageNumber === pageNumber && page.filterKey === filterKey
    );

    if (cachedPage && !force) {
      setCampaignHistoryState((current) => ({
        ...current,
        error: "",
        campaigns: cachedPage.campaigns,
        nextCursor: cachedPage.nextCursor,
        hasMore: cachedPage.hasMore,
        currentPage: pageNumber,
        totalCount: cachedPage.totalCount,
      }));
      return;
    }

    const previousPage = pagesRef.current.find(
      (page) => page.pageNumber === pageNumber - 1 && page.filterKey === filterKey
    );
    if (pageNumber > 1 && !previousPage?.nextCursor) return;

    const cursor = pageNumber === 1 ? "" : previousPage.nextCursor;
    setCampaignHistoryState((current) => ({
      ...current,
      loading: pageNumber === 1,
      loadingPage: pageNumber !== 1,
      error: "",
    }));

    try {
      const campaignResult = await getCologuardCampaigns({
        campaignDesignator: normalizedFilters.designator,
        campaignStatus: normalizedFilters.status,
        createdFrom: normalizedFilters.createdFrom,
        createdTo: normalizedFilters.createdTo,
        limit: PAGE_SIZE,
        cursor,
        accessToken,
      });
      const campaigns = campaignResult.campaigns || [];
      const designators = [
        ...new Set(campaigns.map((campaign) => campaign.campaign_designator).filter(Boolean)),
      ];
      const metricsResult = designators.length
        ? await getCologuardDashboardMetrics({ campaignDesignators: designators, accessToken })
        : { campaigns: [] };
      const page = {
        pageNumber,
        filterKey,
        cursor,
        nextCursor: campaignResult.next_cursor || "",
        hasMore: Boolean(campaignResult.has_more),
        totalCount: campaignResult.total_count ?? null,
        campaigns: mergeCampaignMetrics(campaigns, metricsResult.campaigns || []),
      };

      setCampaignHistoryState((current) => {
        const otherPages = current.pages.filter(
          (cached) => cached.filterKey === filterKey && cached.pageNumber !== pageNumber
        );
        const pages = [...otherPages, page].sort((left, right) => left.pageNumber - right.pageNumber);
        pagesRef.current = pages;
        return {
          loading: false,
          loadingPage: false,
          error: "",
          campaigns: page.campaigns,
          nextCursor: page.nextCursor,
          hasMore: page.hasMore,
          currentPage: pageNumber,
          pages,
          totalCount: page.totalCount,
          pageSize: PAGE_SIZE,
        };
      });
    } catch (error) {
      setCampaignHistoryState((current) => ({
        ...current,
        loading: false,
        loadingPage: false,
        error: error.message,
      }));
    }
  }, [accessToken, isAuthenticated]);

  const updateHistoryFilter = useCallback((name, value) => {
    setHistoryFilters((current) => ({ ...current, [name]: value }));
  }, []);

  const clearHistoryFilters = useCallback(() => {
    setHistoryFilters(INITIAL_HISTORY_FILTERS);
    loadCampaignHistory(INITIAL_HISTORY_FILTERS, { pageNumber: 1, force: true });
  }, [loadCampaignHistory]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadCampaignHistory();
  }, [isAuthenticated, loadCampaignHistory]);

  return {
    historyFilters,
    campaignHistoryState,
    updateHistoryFilter,
    clearHistoryFilters,
    loadCampaignHistory,
  };
}
